import { createSlice } from '@reduxjs/toolkit';
import { mergeReduxStateData } from 'library/helpers/reduxHelpers';

const initialState = {
	data: {},
	timeStamp: null,

	loading: false,
	createBookingLoading: false,
	updateBookingLoading: false,
	deleteBookingLoading: false,
	bookingsLoadingComplete: false,

	// Room/Space booking (Workstream A surface): pending approval queue + create/approve/reject/cancel.
	// roomData is a SEPARATE store from `data` — room bookings come from /booking/rooms, not the legacy
	// /booking list (which excludes them via the ResourceID partition).
	roomData: {}, // org-scoped: roomData[orgID] = [...room bookings]
	roomLoading: false,
	roomLoadingComplete: false,
	pending: {}, // org-scoped pending list: pending[orgID] = [...]
	rescheduleSnapshots: {}, // bookingID → pre-move {startTime,endTime,resourceID,resourceName} for optimistic rollback
	bookingEvents: [], // Booking Log — recent booking domain events (audit trail)
	bookingEventsLoading: false,
	pendingLoading: false,
	pendingLoadingComplete: false,
	createRoomBookingLoading: false,
	lastCreated: null, // result of the most recent operator create ({ bookingID, status, recurrence }) — drives the wizard success panel
	confirmEmailSending: false, // send-confirmation email in flight
	checkinToken: null, // last minted check-in token (for the QR modal)
	checkinTokenLoading: false,
	roomActionLoading: false, // approve/reject/cancel in flight
	roomActionComplete: false, // one-shot success signal for approve/reject/cancel/create

	error: null,
	searchable: true
};

const bookingSlice = createSlice({
	name: 'bookings',
	initialState,
	reducers: {
		// 🔥 Load Bookings
		loadBookings: (state) => {
			state.loading = true;
			state.error = null;
		},
		loadBookingsSuccess: (state, action) => {
			state.timeStamp = Date.now();
			state.loading = false;

			const orgID = action.payload.orgID;
			const oldBookingsData = state.data[orgID] || [];

			// Merge bookings data using 'bookingID' as the unique identifier
			state.data[orgID] = mergeReduxStateData(oldBookingsData, action.payload.data, 'bookingID');

			state.bookingsLoadingComplete = true;
		},
		loadBookingsError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
		},

		// 🔥 Create Booking
		createBooking: (state) => {
			state.createBookingLoading = true;
			state.error = null;
		},
		createBookingSuccess: (state) => {
			state.createBookingLoading = false;
		},
		createBookingError: (state, action) => {
			state.createBookingLoading = false;
			state.error = action.payload;
		},

		// 🔥 Update Booking
		updateBooking: (state) => {
			state.updateBookingLoading = true;
			state.updateBookingComplete = false; // 🔄 resetea
			state.error = null;
		},
		updateBookingSuccess: (state) => {
			state.updateBookingLoading = false;
			state.updateBookingComplete = true; // ✅ señal de éxito
		},
		updateBookingError: (state, action) => {
			state.updateBookingLoading = false;
			state.updateBookingComplete = false;
			state.error = action.payload;
		},
		resetUpdateBookingState: (state) => {
			state.updateBookingComplete = false;
			state.updateBookingLoading = false;
			state.error = null;
		},

		// 🔥 Delete Booking
		deleteBooking: (state) => {
			state.deleteBookingLoading = true;
			state.error = null;
		},
		deleteBookingSuccess: (state) => {
			state.deleteBookingLoading = false;
			state.deleteBookingComplete = true;
		},
		deleteBookingError: (state, action) => {
			state.deleteBookingLoading = false;
			state.error = action.payload;
		},
		clearDeleteBookingState: (state) => {
			state.deleteBookingComplete = false;
			state.error = null;
		},

		// ─── Room/Space booking ────────────────────────────────────────────────
		// 🔥 Load all room bookings (GET /booking/rooms) — the operator table source
		loadRoomBookings: (state) => {
			state.roomLoading = true;
			state.error = null;
		},
		loadRoomBookingsSuccess: (state, action) => {
			state.roomLoading = false;
			state.roomData[action.payload.orgID] = action.payload.data || [];
			state.roomLoadingComplete = true;
		},
		loadRoomBookingsError: (state, action) => {
			state.roomLoading = false;
			state.error = action.payload;
		},

		// 🔥 Booking Log — booking domain events (audit trail)
		loadBookingEvents: (state) => {
			state.bookingEventsLoading = true;
			state.error = null;
		},
		loadBookingEventsSuccess: (state, action) => {
			state.bookingEventsLoading = false;
			state.bookingEvents = action.payload.data || [];
		},
		loadBookingEventsError: (state, action) => {
			state.bookingEventsLoading = false;
			state.error = action.payload;
		},

		// 🔥 Load pending approval queue
		loadPendingBookings: (state) => {
			state.pendingLoading = true;
			state.error = null;
		},
		loadPendingBookingsSuccess: (state, action) => {
			state.pendingLoading = false;
			state.pending[action.payload.orgID] = action.payload.data || [];
			state.pendingLoadingComplete = true;
		},
		loadPendingBookingsError: (state, action) => {
			state.pendingLoading = false;
			state.error = action.payload;
		},

		// 🔥 Create a room booking (POST /booking/resource)
		createRoomBooking: (state) => {
			state.createRoomBookingLoading = true;
			state.roomActionComplete = false;
			state.lastCreated = null; // clear the previous result so the wizard reacts only to this create
			state.error = null;
		},
		createRoomBookingSuccess: (state, action) => {
			state.createRoomBookingLoading = false;
			state.roomActionComplete = true;
			state.lastCreated = action.payload || null; // { bookingID, status, recurrence }
		},
		createRoomBookingError: (state, action) => {
			state.createRoomBookingLoading = false;
			state.error = action.payload;
		},

		// 🔥 Reschedule (drag-to-move / resize / cross-resource move) — OPTIMISTIC.
		// Patch roomData in place immediately (the calendar reflects the drop with no round-trip) and stash
		// the pre-move values so a server rejection can roll the block back. `resourceID`/`resourceName` are
		// only present for a cross-resource move.
		rescheduleBooking: (state, action) => {
			const { orgID, bookingID, startTime, endTime, resourceID, resourceName } = action.payload;
			const list = state.roomData[orgID];
			if (!list) return;
			const idx = list.findIndex((b) => b.bookingID === bookingID);
			if (idx === -1) return;
			const b = list[idx];
			state.rescheduleSnapshots[bookingID] = {
				startTime: b.startTime,
				endTime: b.endTime,
				resourceID: b.resourceID,
				resourceName: b.resourceName
			};
			list[idx] = {
				...b,
				startTime,
				endTime,
				...(resourceID != null ? { resourceID, resourceName: resourceName ?? b.resourceName } : {})
			};
			state.error = null;
		},
		rescheduleBookingSuccess: (state, action) => {
			const bookingID = action.payload?.bookingID;
			if (bookingID != null) delete state.rescheduleSnapshots[bookingID];
		},
		rescheduleBookingError: (state, action) => {
			const { orgID, bookingID } = action.payload || {};
			const snap = state.rescheduleSnapshots[bookingID];
			const list = orgID != null ? state.roomData[orgID] : null;
			if (snap && list) {
				const idx = list.findIndex((b) => b.bookingID === bookingID);
				if (idx !== -1) list[idx] = { ...list[idx], ...snap };
			}
			delete state.rescheduleSnapshots[bookingID];
			state.error = action.payload?.error || null;
		},

		// 🔥 Send a booking confirmation email (+ ICS)
		sendBookingConfirmation: (state) => {
			state.confirmEmailSending = true;
			state.error = null;
		},
		sendBookingConfirmationSuccess: (state) => {
			state.confirmEmailSending = false;
		},
		sendBookingConfirmationError: (state, action) => {
			state.confirmEmailSending = false;
			state.error = action.payload;
		},

		// 🔥 Approve / reject / cancel — share the roomAction flags
		approveBooking: (state) => {
			state.roomActionLoading = true;
			state.roomActionComplete = false;
			state.error = null;
		},
		rejectBooking: (state) => {
			state.roomActionLoading = true;
			state.roomActionComplete = false;
			state.error = null;
		},
		cancelRoomBooking: (state) => {
			state.roomActionLoading = true;
			state.roomActionComplete = false;
			state.error = null;
		},
		markNoShow: (state) => {
			state.roomActionLoading = true;
			state.roomActionComplete = false;
			state.error = null;
		},
		// 🔥 Check a booking in (operator) — shares the roomAction flags (reloads on success)
		checkInBooking: (state) => {
			state.roomActionLoading = true;
			state.roomActionComplete = false;
			state.error = null;
		},
		// 🔥 Mint a booking's check-in QR token
		loadCheckinToken: (state) => {
			state.checkinTokenLoading = true;
			state.checkinToken = null;
			state.error = null;
		},
		loadCheckinTokenSuccess: (state, action) => {
			state.checkinTokenLoading = false;
			state.checkinToken = action.payload || null;
		},
		loadCheckinTokenError: (state, action) => {
			state.checkinTokenLoading = false;
			state.error = action.payload;
		},
		clearCheckinToken: (state) => {
			state.checkinToken = null;
		},
		roomActionSuccess: (state) => {
			state.roomActionLoading = false;
			state.roomActionComplete = true;
		},
		roomActionError: (state, action) => {
			state.roomActionLoading = false;
			state.error = action.payload;
		},
		resetRoomActionState: (state) => {
			state.roomActionComplete = false;
			state.roomActionLoading = false;
			state.lastCreated = null;
			state.error = null;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => {
			return initialState; // Reset state on logout
		});
	}
});

// Export the actions
export const bookingActions = bookingSlice.actions;

// Export the reducer
export default bookingSlice.reducer;
